import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';

import AddBtn from '../../../../../components/Buttons/AddBtn';
import Btn from '../../../../../components/Buttons/Btn';
import Input from '../../../../../components/Input';
import InputFile from '../../../../../components/InputFile';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import TablePaginator from '../../../../../components/TablePaginator';
import { ActionButtonsContainer } from '../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../enums/httpStatus';
import { DeleteIcon } from '../../../../../icons';
import {
  audienceAPI,
  tenantCustomLibsVersionsApi as mainAPi,
  SmartsellEnvApi,
  tenantCustomLibsApi,
} from '../../../../../services/requests';
import { Audience } from '../../../../../types';
import {
  TenantCustomLib,
  TenantCustomLibVersion,
} from '../../../../../types/apiResponse/TenantCustomLibs';
import { SmartsellEnv, SmartsellEnvObj } from '../../../../../types/smartsell';
import {
  getOptionsFromAudiences,
  SelectOptionSpecValue,
} from '../../../../../utils/getSelectOptions';
// eslint-disable-next-line max-len
import { firstLetterLow } from '../../../../../utils/helpers';
// eslint-disable-next-line max-len
import ModalCreateAudience from '../../../Registrations/Audience/Components/ModalCreate/CreateAudienceModal';

type Props = {
  closeModal: () => void;
  reqCallback: () => void;
  customLibToUpdate?: TenantCustomLibVersion | null;
  isNewVersion?: boolean;
};

type Option = {
  value: any;
  label: string;
};

const ModalCreateJSX = ({
  closeModal,
  reqCallback,
  customLibToUpdate,
  isNewVersion = false,
}: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const [smartsellEnv, setSmartsellEnv] = useState<SelectOptionSpecValue<SmartsellEnv> | null>(
    null,
  );
  const [lib, setLib] = useState<SelectOptionSpecValue<TenantCustomLib> | null>(null);
  const [description, setDescription] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [mainFile, setMainFile] = useState<HTMLInputElement | null>(null);
  const [auxiliaryFile, setAuxiliaryFile] = useState<HTMLInputElement | null>(null);
  const [selectedAudience, setSelectedAudience] = useState<Option | Record<string, unknown>>({});

  const [showModalNewAudience, setShowModalNewAudience] = useState(false);

  const [addedAudiences, setAddedAudience] = useState<Audience[]>([]);
  const [selectAudienceCache, setSelectAudienceCache] = useState(0);

  useEffect(() => {
    if (customLibToUpdate) {
      setSmartsellEnv({
        label: t(customLibToUpdate.lib.smartsell_env),
        value: customLibToUpdate.lib.smartsell_env,
      });
      setLib({
        label: t(customLibToUpdate.lib.name),
        value: customLibToUpdate.lib,
      });
      setDescription(customLibToUpdate.description);
      setModuleId(customLibToUpdate.module_id || '');
      if (customLibToUpdate.audiences) {
        const audiencesToAdded: Audience[] = [];
        customLibToUpdate.audiences.forEach((item) => {
          audiencesToAdded.push({
            id: item.audience.id,
            description: item.audience.description,
          });
        });
        setAddedAudience(audiencesToAdded);
      }
    }
  }, [customLibToUpdate]);

  const reqCreate = async () => {
    const isRequiredAudience = lib?.value?.is_required_audience ?? false;
    if (addedAudiences.length <= 0 && isRequiredAudience) {
      frontendNotification({
        message: t('addAtLeastOneAudience'),
        type: 'warning',
      });
      return;
    }

    if (!lib) {
      frontendNotification({
        message: t('selectTheLib'),
        type: 'warning',
      });
      return;
    }

    if (lib.value.name === 'SmartsellSalesSyncSource' && !moduleId) {
      frontendNotification({
        message: 'Informe a tabela da fonte de sincronização.',
        type: 'warning',
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await mainAPi.postWithFile(
        mainFile,
        'file',
        {
          libName: lib.value.name,
          moduleId,
          description,
          audiencesAsString: addedAudiences.map((item) => item.id).toString(),
          mainFile,
          auxiliaryFile,
        },
        false,
      );

      if (res.status === HttpStatus.OK) {
        frontendNotification({
          message: t('successfullyCreated'),
          type: 'success',
        });

        reqCallback();
      } else {
        throw res;
      }
    } catch (err) {
      if (!mainAPi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }
    setIsLoading(false);
  };

  const reqUpdate = async () => {
    if (!customLibToUpdate) {
      return;
    }
    const isRequiredAudience = customLibToUpdate.lib.is_required_audience ?? false;
    if (addedAudiences.length <= 0 && isRequiredAudience) {
      frontendNotification({
        message: t('addAtLeastOneAudience'),
        type: 'warning',
      });
      return;
    }

    try {
      setIsLoading(true);
      mainAPi.query = '';
      const res = await mainAPi.update(
        {
          description,
          audiencesAsString: addedAudiences.map((item) => item.id).toString(),
        },
        customLibToUpdate.id,
      );

      if (res.status === HttpStatus.OK) {
        frontendNotification({
          message: t('success'),
          type: 'success',
        });

        reqCallback();
      } else {
        throw res;
      }
    } catch (err) {
      if (!mainAPi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAudienceBtn = () => {
    if (!selectedAudience.value) {
      frontendNotification({
        message: t('selectAnAudience'),
        type: 'warning',
      });
      return;
    }

    addAudience(selectedAudience.value, selectedAudience.label as string);
  };

  const addAudience = (audienceId: number, audienceDescription: string) => {
    const copyAddedAudiences = [...addedAudiences];

    if (copyAddedAudiences.some((addedAudience) => addedAudience.id === audienceId)) {
      frontendNotification({
        message: t('alreadyExists'),
        type: 'warning',
      });
    } else {
      copyAddedAudiences.push({
        id: audienceId,
        description: audienceDescription,
      });

      setAddedAudience(copyAddedAudiences);
      setSelectedAudience({});
    }
  };

  const removeAudi = (audienceToRemove: Audience) => {
    const filteredAudience = addedAudiences.filter(
      (addedAudience) => JSON.stringify(addedAudience) !== JSON.stringify(audienceToRemove),
    );

    setAddedAudience(filteredAudience);
  };

  const actionsButtons = (row: Row<Audience>) => (
    <ActionButtonsContainer type={1}>
      <DeleteIcon size="1.5rem" title={t('edit')} onClick={() => removeAudi(row.original)} />
    </ActionButtonsContainer>
  );

  const columns: Column<Audience>[] = useMemo(
    () => [
      {
        Header: t('id') as string,
        accessor: 'id',
      },
      {
        Header: t('description') as string,
        accessor: 'description',
      },
      {
        id: 'actionsbtn',
        Cell: ({ row }: any) => actionsButtons(row),
      },
    ],
    [addedAudiences],
  );

  return (
    <Modal
      title={t('addNewLibraryVersion')}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('save')}
      action={customLibToUpdate && !isNewVersion ? reqUpdate : reqCreate}
      isLoading={isLoading}
      style={{ width: '65rem' }}
      bodyStyle={{ overflow: 'scroll', width: '100%' }}
    >
      <div className="form">
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="smartsellEnv">{t('smartsellEnv') as string}</label>
            <SelectAsyncJSX
              value={smartsellEnv}
              onChange={setSmartsellEnv}
              request={SmartsellEnvApi}
              reqResponseToOption={{
                mapper: {
                  label: (item: SmartsellEnvObj) => item.label,
                  value: (item: SmartsellEnvObj) => item.name,
                },
              }}
              placeholder={t('smartsellEnv')}
              id="smartsellEnv"
              disabled={isLoading || customLibToUpdate !== null}
            />
          </div>
          <div className="form-row-item">
            <label htmlFor="customLib">{t('lib') as string}</label>
            <SelectAsyncJSX
              value={lib}
              onChange={setLib}
              request={tenantCustomLibsApi}
              reqResponseToOption={{
                mapper: {
                  label: (item: TenantCustomLib) => t(firstLetterLow(item.name)),
                  value: (item: TenantCustomLib) => item,
                },
              }}
              placeholder={t('lib')}
              query={smartsellEnv ? `smartsell_env=${smartsellEnv.value}` : ''}
              cacheUniqs={[smartsellEnv]}
              id="customLib"
              disabled={isLoading || customLibToUpdate !== null}
            />
          </div>
          {lib?.value.name === 'SmartsellSalesSyncSource' && (
            <div className="form-row-item">
              <label htmlFor="customLibModuleId">Tabela (module_id)</label>
              <Input
                value={moduleId}
                onChange={(e: any) => setModuleId(e.target.value)}
                placeholder="clientes, limitecliente, titulos, parametros ou parametrosvendedor"
                disabled={isLoading || (customLibToUpdate !== null && !isNewVersion)}
                id="customLibModuleId"
              />
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="customLibDescription">{t('description') as string}</label>
            <Input
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
              placeholder={t('description')}
              disabled={isLoading}
              id="customLibDescription"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="customLibMainFile">
              {`${t('mainFile')} (${lib ? lib.value.allowed_file_types : ''})`}
            </label>
            <InputFile
              value={mainFile}
              setFile={setMainFile}
              disabled={isLoading || !lib || (customLibToUpdate !== null && !isNewVersion)}
              accept={lib ? lib.value.allowed_file_types : ''}
              id="customLibMainFile"
            />
          </div>

          <div className="form-row-item">
            <label htmlFor="customLibAuxiliaryFile">{t('auxiliaryFile')}</label>
            <InputFile
              value={auxiliaryFile}
              setFile={setAuxiliaryFile}
              disabled={isLoading || !lib || (customLibToUpdate !== null && !isNewVersion)}
              id="customLibAuxiliaryFile"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="customLibAudience">{t('audience') as string}</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SelectAsyncJSX
                value={selectedAudience}
                onChange={setSelectedAudience}
                request={audienceAPI}
                reqResponseToOption={getOptionsFromAudiences}
                disabled={isLoading}
                placeholder={t('audience')}
                cacheUniqs={[selectAudienceCache]}
                id="customLibAudience"
              />
              <AddBtn style={{ marginLeft: '1rem' }} onClick={() => handleAddAudienceBtn()} />
            </div>
          </div>

          <div className="form-row-item-label-off">
            <Btn
              text={t('newAudience')}
              disabled={isLoading}
              onClick={() => setShowModalNewAudience(true)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <TablePaginator
              data={addedAudiences}
              columns={columns}
              globalFiltering
              style={{ width: '100%', marginTop: '2rem' }}
            />
          </div>
        </div>

        {showModalNewAudience && (
          <ModalCreateAudience
            closeModal={() => setShowModalNewAudience(false)}
            callback={() => setSelectAudienceCache(selectAudienceCache + 1)}
            availableTypes={['seller']}
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalCreateJSX;
